import { useEffect, useState } from "react";
import * as RG from "@radix-ui/react-radio-group";
import { motion } from "framer-motion";
import { Copy, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useCourtyard } from "@/context/CourtyardContext";
import { errMsg, http } from "@/lib/api";
import { copyText, scrollToId } from "@/lib/event";

const RESPONSES = [
  { id: "yes", label: "I’ll be there", hint: "Diya ready rakhna" },
  { id: "maybe", label: "Maybe", hint: "Dil se haan, calendar se pending" },
  { id: "no", label: "Can’t make it", hint: "Agli baar pakka" },
];
const blank = { name: "", response: "", party_size: 1, dietary: "", note: "" };

export function useLenisPause(open) {
  useEffect(() => {
    if (!open) return undefined;
    window.__lenis?.stop();
    return () => window.__lenis?.start();
  }, [open]);
}

function Sparkle() {
  return (
    <div className="pointer-events-none absolute left-1/2 top-10 h-0 w-0" aria-hidden="true">
      {Array.from({ length: 14 }).map((_, i) => {
        const a = (i / 14) * Math.PI * 2;
        return (
          <motion.span key={i} className="absolute h-3.5 w-3.5 rounded-full border-2 border-[#142344]" style={{ background: i % 2 ? "#FFC83D" : "#D81B60" }}
            initial={{ x: 0, y: 0, opacity: 1, scale: 1 }} animate={{ x: Math.cos(a) * 110, y: Math.sin(a) * 70, opacity: 0, scale: 0.3 }}
            transition={{ duration: 1.1, ease: "easeOut" }} />
        );
      })}
    </div>
  );
}

function Done({ rsvp, onClose }) {
  const link = `${window.location.origin}/?rsvp=${rsvp.edit_token}`;
  const copy = async () => (await copyText(link)) ? toast.success("Private edit link copied") : toast.error("Couldn't copy. Please select and copy it manually.");
  const msg = {
    yes: ["Your place in the courtyard is saved.", "Diya tumhare naam ka jal raha hai. See you there!"],
    maybe: ["Your maybe is noted.", "We’ll keep a diya ready, just in case. Update anytime with your link."],
    no: ["You’ll be missed.", "Sending a little Diwali glow your way."],
  }[rsvp.response];
  return (
    <div className="relative text-center" data-testid="rsvp-confirmation">
      <Sparkle />
<motion.div initial={{ scale: 0.6, rotate: -8 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 260, damping: 14 }} className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full border-[3px] border-[var(--navy)] bg-[var(--yellow)] shadow-[4px_4px_0_#142344]" aria-hidden="true"><span className="font-poster text-3xl text-[var(--pink)]">{rsvp.response === "no" ? "♥" : "✓"}</span></motion.div>
      <p className="eyebrow">{rsvp.response === "no" ? "Noted, with love" : "RSVP saved"}</p>
      <DialogTitle className="font-poster mt-3 text-[2.5rem] leading-[0.95] text-[var(--navy)]">{msg[0]}</DialogTitle>
      <DialogDescription className="mt-2 text-base text-[var(--ink)]">{msg[1]}</DialogDescription>
      <div className="pop-sm mt-6 bg-white p-4 text-left">
        <p className="text-sm font-semibold">Your private edit link</p>
        <p className="mt-1 text-xs text-[#3d5a51]">Keep this to yourself. Anyone with it can change your RSVP. This browser remembers it too.</p>
        <div className="mt-3 flex gap-2">
          <input readOnly value={link} className="field min-h-[44px] flex-1 text-xs" aria-label="Private edit link" onFocus={(e) => e.target.select()} data-testid="rsvp-edit-link" />
          <button type="button" onClick={copy} className="btn-ghost min-h-[44px] px-4" data-testid="copy-edit-link-button"><Copy className="h-4 w-4" aria-hidden="true" /><span className="sr-only">Copy edit link</span></button>
        </div>
      </div>
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
        {rsvp.response !== "no" && (
          <button type="button" className="btn-primary" onClick={() => { onClose(); setTimeout(() => scrollToId("my-pass"), 250); }} data-testid="go-to-pass-button">
            See my pass
          </button>
        )}
        <button type="button" className="btn-ghost" onClick={onClose} data-testid="rsvp-done-button">Done</button>
      </div>
    </div>
  );
}

export default function RsvpModal() {
  const { rsvpOpen, setRsvpOpen, rsvp, saveRsvp } = useCourtyard();
  const [view, setView] = useState("form");
  const [form, setForm] = useState(blank);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState("");
  useLenisPause(rsvpOpen);

  useEffect(() => {
    if (!rsvpOpen) return;
    setView("form");
    setErrors({});
    setServerError("");
    setForm(rsvp ? { name: rsvp.name, response: rsvp.response, party_size: rsvp.party_size || 1, dietary: rsvp.dietary, note: rsvp.note } : blank);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rsvpOpen]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const attending = form.response === "yes" || form.response === "maybe";

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const errs = {};
    if (!form.name.trim()) errs.name = "Naam toh batao. The host needs to know who’s coming.";
    if (!form.response) errs.response = "Pick one: coming, maybe, or can’t make it.";
    const n = Number(form.party_size);
    if (attending && (!Number.isInteger(n) || n < 1 || n > 20)) errs.party_size = "Enter between 1 and 20 people (including you).";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    setServerError("");
    try {
      const body = { ...form, name: form.name.trim(), party_size: attending ? n : 0 };
      const r = rsvp ? await http.put(`/rsvp/${rsvp.edit_token}`, body) : await http.post("/rsvp", body);
      saveRsvp(r.data);
      setView("done");
    } catch (err) {
      setServerError(errMsg(err, "Your RSVP didn’t save. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={rsvpOpen} onOpenChange={setRsvpOpen}>
      <DialogContent
        data-lenis-prevent
        className="top-0 h-[100dvh] max-h-[100dvh] max-w-lg translate-y-0 overflow-y-auto overscroll-contain rounded-none border-0 bg-[var(--ivory)] p-6 pb-10 text-[var(--ink)] sm:top-[50%] sm:h-auto sm:max-h-[92dvh] sm:translate-y-[-50%] sm:rounded-[24px] sm:border-[3px] sm:border-[var(--navy)] sm:p-8 sm:shadow-[8px_8px_0_#142344]"
        data-testid="rsvp-modal"
      >
        {view === "done" && rsvp ? (
          <Done rsvp={rsvp} onClose={() => setRsvpOpen(false)} />
        ) : (
          <form onSubmit={submit} noValidate className="flex flex-col gap-6">
            <div>
              <p className="eyebrow">{rsvp ? "Update your RSVP" : "RSVP"}</p>
              <DialogTitle className="font-poster mt-2 text-5xl leading-[0.95] text-[var(--navy)]">Aa rahe ho <span className="font-serif-i text-[var(--pink)]">na?</span></DialogTitle>
              <DialogDescription className="mt-1 text-[var(--ink)]">Only the host sees your answers.</DialogDescription>
            </div>
            <div>
              <label htmlFor="rsvp-name" className="font-semibold">Your name</label>
              <input id="rsvp-name" className="field mt-2" value={form.name} onChange={(e) => set("name", e.target.value)} maxLength={80} autoComplete="name" aria-invalid={!!errors.name} aria-describedby={errors.name ? "rsvp-name-err" : undefined} data-testid="rsvp-name-input" />
              {errors.name && <p id="rsvp-name-err" className="mt-1 text-sm font-medium text-[#9b2c2c]">{errors.name}</p>}
            </div>
            <fieldset>
              <legend className="font-semibold">Will you join us?</legend>
              <RG.Root value={form.response} onValueChange={(v) => set("response", v)} className="mt-2 grid gap-2" aria-invalid={!!errors.response}>
                {RESPONSES.map((o) => (
                  <RG.Item key={o.id} value={o.id} className="flex min-h-[58px] items-center justify-between rounded-xl border-2 border-[var(--navy)] bg-white px-4 text-left shadow-[3px_3px_0_#142344] transition-all active:translate-x-[2px] active:translate-y-[2px] active:shadow-none data-[state=checked]:bg-[var(--pink)] data-[state=checked]:text-[var(--cream)]" data-testid={`rsvp-response-${o.id}`}>
                    <span><span className="block font-semibold">{o.label}</span><span className="block text-xs opacity-80">{o.hint}</span></span>
                    <span className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-current"><RG.Indicator className="h-2.5 w-2.5 rounded-full bg-[var(--yellow)]" /></span>
                  </RG.Item>
                ))}
              </RG.Root>
              {errors.response && <p className="mt-1 text-sm font-medium text-[#9b2c2c]">{errors.response}</p>}
            </fieldset>
            {attending && (
              <>
                <div>
                  <label htmlFor="rsvp-count" className="font-semibold">How many people, including you?</label>
                  <div className="mt-2 flex items-center gap-3">
                    <button type="button" className="btn-ghost h-12 w-12 px-0" onClick={() => set("party_size", Math.max(1, Number(form.party_size) - 1))} aria-label="One fewer person" data-testid="rsvp-count-minus"><Minus className="h-4 w-4" /></button>
                    <input id="rsvp-count" type="number" inputMode="numeric" min={1} max={20} className="field w-20 text-center text-lg font-semibold" value={form.party_size} onChange={(e) => set("party_size", e.target.value === "" ? "" : Number(e.target.value))} data-testid="rsvp-count-input" />
                    <button type="button" className="btn-ghost h-12 w-12 px-0" onClick={() => set("party_size", Math.min(20, Number(form.party_size) + 1))} aria-label="One more person" data-testid="rsvp-count-plus"><Plus className="h-4 w-4" /></button>
                  </div>
                  {errors.party_size && <p className="mt-1 text-sm font-medium text-[#9b2c2c]">{errors.party_size}</p>}
                </div>
                <div>
                  <label htmlFor="rsvp-diet" className="font-semibold">Dietary requirements <span className="font-normal text-[#3d5a51]">(optional)</span></label>
                  <textarea id="rsvp-diet" rows={2} maxLength={300} className="field mt-2" placeholder="Jain, vegan, no nuts, extra mithai…" value={form.dietary} onChange={(e) => set("dietary", e.target.value)} data-testid="rsvp-dietary-input" />
                </div>
              </>
            )}
            <div>
              <label htmlFor="rsvp-note" className="font-semibold">A note for the host <span className="font-normal text-[#3d5a51]">(optional)</span></label>
              <textarea id="rsvp-note" rows={2} maxLength={500} className="field mt-2" value={form.note} onChange={(e) => set("note", e.target.value)} data-testid="rsvp-note-input" />
            </div>
            {serverError && <p role="alert" className="rounded-xl bg-[#fbe9e6] p-3 text-sm font-medium text-[#8a2525]" data-testid="rsvp-error">{serverError}</p>}
            <button type="submit" className="btn-pink w-full text-lg" disabled={busy} data-testid="rsvp-submit-button">
              {busy ? "Saving…" : rsvp ? "Update my RSVP" : "Send my RSVP"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
